import { useState, useEffect } from "react";
import { 
  FileText, 
  Plus, 
  Trash2, 
  Sparkles,
  Copy,
  Eye,
  Edit
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import { getTemplates, createTemplate, deleteTemplate, generateAITemplate } from "../lib/api";
import { motion } from "framer-motion";

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  const [manualForm, setManualForm] = useState({
    name: "",
    subject: "",
    body: "",
    template_type: "outreach"
  });

  const [aiForm, setAIForm] = useState({
    website_url: "",
    website_niche: "",
    your_name: "",
    your_website: "",
    topic_ideas: "",
    tone: "professional"
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await getTemplates();
      setTemplates(response.data);
    } catch (error) {
      toast.error("Failed to fetch templates");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!manualForm.name.trim() || !manualForm.subject.trim() || !manualForm.body.trim()) {
      toast.error("Please fill all required fields");
      return;
    }

    setCreating(true);
    try {
      await createTemplate(manualForm);
      toast.success("Template created");
      setShowCreateDialog(false);
      setManualForm({ name: "", subject: "", body: "", template_type: "outreach" });
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to create template");
    } finally {
      setCreating(false);
    }
  };

  const handleGenerateAI = async () => {
    if (!aiForm.website_url.trim() || !aiForm.your_name.trim()) {
      toast.error("Please fill website URL and your name");
      return;
    }

    setGenerating(true);
    try {
      await generateAITemplate(aiForm);
      toast.success("AI template generated!");
      setShowAIDialog(false);
      setAIForm({
        website_url: "",
        website_niche: "",
        your_name: "",
        your_website: "",
        topic_ideas: "",
        tone: "professional"
      });
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate template");
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTemplate(id);
      toast.success("Template deleted");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const handleCopy = (template) => {
    const text = `Subject: ${template.subject}\n\n${template.body}`;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handlePreview = (template) => {
    setPreviewTemplate(template);
    setShowPreviewDialog(true);
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Templates</h1>
          <p className="text-muted-foreground mt-1">
            Create and manage email templates for outreach
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="generate-ai-btn">
                <Sparkles className="w-4 h-4 mr-2" />
                Generate with AI
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg" data-testid="ai-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Generate AI Template
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="ai-url">Target Website URL *</Label>
                  <Input
                    id="ai-url"
                    placeholder="e.g., techblog.com"
                    value={aiForm.website_url}
                    onChange={(e) => setAIForm(f => ({ ...f, website_url: e.target.value }))}
                    data-testid="ai-website-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ai-niche">Website Niche</Label>
                  <Input
                    id="ai-niche"
                    placeholder="e.g., Technology, SaaS, Marketing"
                    value={aiForm.website_niche}
                    onChange={(e) => setAIForm(f => ({ ...f, website_niche: e.target.value }))}
                    data-testid="ai-niche-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ai-name">Your Name *</Label>
                    <Input
                      id="ai-name"
                      placeholder="John Doe"
                      value={aiForm.your_name}
                      onChange={(e) => setAIForm(f => ({ ...f, your_name: e.target.value }))}
                      data-testid="ai-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ai-website">Your Website</Label>
                    <Input
                      id="ai-website"
                      placeholder="yoursite.com"
                      value={aiForm.your_website}
                      onChange={(e) => setAIForm(f => ({ ...f, your_website: e.target.value }))}
                      data-testid="ai-your-website-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ai-topics">Topic Ideas</Label>
                  <Textarea
                    id="ai-topics"
                    placeholder="e.g., 10 SEO tips for startups, How to build backlinks..."
                    value={aiForm.topic_ideas}
                    onChange={(e) => setAIForm(f => ({ ...f, topic_ideas: e.target.value }))}
                    data-testid="ai-topics-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tone</Label>
                  <Select 
                    value={aiForm.tone}
                    onValueChange={(v) => setAIForm(f => ({ ...f, tone: v }))}
                  >
                    <SelectTrigger data-testid="ai-tone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="friendly">Friendly</SelectItem>
                      <SelectItem value="casual">Casual</SelectItem>
                      <SelectItem value="formal">Formal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAIDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleGenerateAI} 
                  disabled={generating}
                  data-testid="generate-btn"
                >
                  {generating ? (
                    <>
                      <Sparkles className="w-4 h-4 mr-2 animate-pulse" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-template-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg" data-testid="create-dialog">
              <DialogHeader>
                <DialogTitle>Create Template</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Template Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Guest Post Pitch"
                    value={manualForm.name}
                    onChange={(e) => setManualForm(f => ({ ...f, name: e.target.value }))}
                    data-testid="template-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject Line *</Label>
                  <Input
                    id="subject"
                    placeholder="e.g., Guest Post Opportunity for {{website_name}}"
                    value={manualForm.subject}
                    onChange={(e) => setManualForm(f => ({ ...f, subject: e.target.value }))}
                    data-testid="template-subject-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Available: {"{{website_name}}, {{editor_name}}, {{niche}}, {{sender_name}}"}. {"{{editor_name}}"} becomes "there" when the name is unknown.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="body">Email Body *</Label>
                  <Textarea
                    id="body"
                    className="min-h-[200px] font-mono text-sm"
                    placeholder="Hi {{editor_name}},&#10;&#10;I came across {{website_name}} and..."
                    value={manualForm.body}
                    onChange={(e) => setManualForm(f => ({ ...f, body: e.target.value }))}
                    data-testid="template-body-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Template Type</Label>
                  <Select 
                    value={manualForm.template_type}
                    onValueChange={(v) => setManualForm(f => ({ ...f, template_type: v }))}
                  >
                    <SelectTrigger data-testid="template-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="outreach">Initial Outreach</SelectItem>
                      <SelectItem value="followup">Follow-up</SelectItem>
                      <SelectItem value="thankyou">Thank You</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreate} 
                  disabled={creating}
                  data-testid="save-template-btn"
                >
                  {creating ? "Creating..." : "Create Template"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="loading-skeleton">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="skeleton h-48 rounded-md" />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="empty-state" data-testid="empty-state">
            <FileText className="w-16 h-16" strokeWidth={1} />
            <h3 className="text-lg font-semibold mt-4">No templates yet</h3>
            <p className="text-sm max-w-sm mt-2">
              Create email templates manually or generate them with AI.
            </p>
            <div className="flex gap-2 mt-4">
              <Button 
                variant="outline"
                onClick={() => setShowAIDialog(true)}
                data-testid="empty-ai-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generate with AI
              </Button>
              <Button 
                onClick={() => setShowCreateDialog(true)}
                data-testid="empty-create-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Manually
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
          {templates.map((template, index) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card className="bg-card border-border hover:border-primary/30 transition-colors h-full flex flex-col" data-testid={`template-card-${template.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base truncate flex items-center gap-2">
                        {template.name}
                        {template.is_ai_generated && (
                          <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
                        )}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        {template.subject}
                      </p>
                    </div>
                    <span className="text-xs px-2 py-1 rounded bg-accent text-muted-foreground capitalize flex-shrink-0">
                      {template.template_type}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <p className="text-sm text-muted-foreground line-clamp-3 flex-1">
                    {template.body}
                  </p>
                  
                  {template.variables?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {template.variables.slice(0, 3).map(v => (
                        <span key={v} className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary font-mono">
                          {`{{${v}}}`}
                        </span>
                      ))}
                      {template.variables.length > 3 && (
                        <span className="text-xs text-muted-foreground">
                          +{template.variables.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-1 mt-4 pt-4 border-t border-border">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePreview(template)}
                      data-testid={`preview-${template.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Preview
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(template)}
                      data-testid={`copy-${template.id}`}
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive ml-auto"
                      onClick={() => handleDelete(template.id)}
                      data-testid={`delete-${template.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="sm:max-w-2xl" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
          </DialogHeader>
          {previewTemplate && (
            <div className="space-y-4 py-4">
              <div className="bg-accent/50 rounded-md p-4">
                <Label className="text-muted-foreground">Subject</Label>
                <p className="font-medium mt-1">{previewTemplate.subject}</p>
              </div>
              <div className="bg-accent/50 rounded-md p-4">
                <Label className="text-muted-foreground">Body</Label>
                <pre className="font-mono text-sm mt-2 whitespace-pre-wrap">
                  {previewTemplate.body}
                </pre>
              </div>
              {previewTemplate.variables?.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Variables</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {previewTemplate.variables.map(v => (
                      <span key={v} className="text-sm px-2 py-1 rounded bg-primary/10 text-primary font-mono">
                        {`{{${v}}}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
            <Button onClick={() => previewTemplate && handleCopy(previewTemplate)}>
              <Copy className="w-4 h-4 mr-2" />
              Copy Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
